import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, API } from "@/lib/api";
import { LOGO_URL, REGIONS } from "@/lib/constants";
import { toast } from "sonner";
import {
  LayoutDashboard, Newspaper, Calendar, MapPin, Users, Mail, Send,
  FilePlus, LogOut, Trash2, Pencil, Download, Plus, X, Loader2, Inbox, Building2,
  Image as ImageIcon, Settings, Upload, Copy, Check, CreditCard, Share2,
  Facebook, Instagram, Twitter, Youtube, Music2, CheckCircle2, XCircle, UserSquare2, IdCard,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { MemberCard } from "@/components/MemberCard";

const MEDIA_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const mediaUrl = (relative) => `${MEDIA_BASE}${relative}`;

const NAV = [
  { key: "overview", label: "Vue d'ensemble", Icon: LayoutDashboard },
  { key: "articles", label: "Contenus", Icon: Newspaper },
  { key: "events", label: "Événements", Icon: Calendar },
  { key: "committees", label: "Comités", Icon: MapPin },
  { key: "bureau", label: "Le bureau", Icon: UserSquare2 },
  { key: "memberships", label: "Adhésions", Icon: Users },
  { key: "contacts", label: "Messages", Icon: Mail },
  { key: "newsletter", label: "Newsletter", Icon: Send },
  { key: "requests", label: "Demandes comités", Icon: Building2 },
  { key: "media", label: "Médias", Icon: ImageIcon },
  { key: "social", label: "Publications", Icon: Share2 },
  { key: "settings", label: "Paramètres", Icon: Settings },
];

function exportCSV(rows, filename) {
  if (!rows.length) return toast.error("Aucune donnée à exporter.");
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
  ].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-60 bg-brand-blue text-white flex flex-col fixed h-full">
        <div className="p-5 border-b border-blue-400/30">
          <img src={LOGO_URL} alt="DEFAR" className="h-10 bg-white p-1 rounded-sm" />
          <p className="mt-3 text-xs text-blue-200 uppercase tracking-wider">Back-office</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map(({ key, label, Icon }) => (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setTab(key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-sm transition-colors ${
                tab === key ? "bg-brand-green text-white" : "text-blue-100 hover:bg-blue-800/40"
              }`}
            >
              <Icon size={18} /> {label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-blue-400/30">
          <p className="px-3 text-xs text-blue-200 mb-2 truncate">{user?.email}</p>
          <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-blue-100 hover:bg-blue-800/40 rounded-sm">
            <LogOut size={18} /> Déconnexion
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-60 p-6 lg:p-8">
        {tab === "overview" && <Overview />}
        {tab === "articles" && <Articles />}
        {tab === "events" && <Events />}
        {tab === "committees" && <Committees />}
        {tab === "bureau" && <BureauTab />}
        {tab === "memberships" && <Memberships />}
        {tab === "contacts" && <Contacts />}
        {tab === "newsletter" && <Newsletter />}
        {tab === "requests" && <Requests />}
        {tab === "media" && <Media />}
        {tab === "social" && <SocialPublisher />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}

const Title = ({ children, action }) => (
  <div className="flex items-center justify-between mb-6">
    <h1 className="text-2xl font-bold text-slate-900">{children}</h1>
    {action}
  </div>
);

const useData = (url) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    setLoading(true);
    api.get(url).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [url]);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, reload, setData };
};

// ---------------- Overview ----------------
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  if (!stats) return <Loader />;

  const cards = [
    { label: "Membres", value: stats.members, Icon: Users, color: "bg-brand-blue" },
    { label: "Adhérents", value: stats.adherents, Icon: Users, color: "bg-brand-green" },
    { label: "Articles", value: stats.articles, Icon: Newspaper, color: "bg-blue-600" },
    { label: "Comités", value: stats.committees, Icon: MapPin, color: "bg-emerald-600" },
    { label: "Messages", value: stats.contacts, Icon: Mail, color: "bg-indigo-600", sub: `${stats.unread} non lus` },
    { label: "Newsletter", value: stats.newsletter, Icon: Send, color: "bg-teal-600" },
    { label: "Événements", value: stats.events, Icon: Calendar, color: "bg-cyan-700" },
    { label: "Demandes comités", value: stats.pending_requests, Icon: Building2, color: "bg-amber-600", sub: "en attente" },
  ];

  return (
    <div>
      <Title>Vue d'ensemble</Title>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-border rounded-sm p-5" data-testid={`stat-${c.label}`}>
            <div className={`h-10 w-10 flex items-center justify-center text-white ${c.color}`}>
              <c.Icon size={20} />
            </div>
            <p className="mt-4 text-3xl font-bold text-slate-900">{c.value}</p>
            <p className="text-sm text-slate-500">{c.label}</p>
            {c.sub && <p className="text-xs text-amber-600 mt-1">{c.sub}</p>}
          </div>
        ))}
      </div>

      <div className="mt-8 bg-white border border-border rounded-sm p-6">
        <h3 className="font-bold text-slate-900 mb-4">Adhésions par région</h3>
        {stats.by_region.length === 0 ? (
          <p className="text-sm text-slate-500">Aucune donnée.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.by_region}>
              <XAxis dataKey="region" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <RTooltip />
              <Bar dataKey="count" fill="#064E3B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ---------------- Articles ----------------
const emptyArticle = { title: "", category: "actualite", summary: "", content: "", image_url: "", status: "published" };
function Articles() {
  const { data, loading, reload } = useData("/admin/articles");
  const [modal, setModal] = useState(null); // article or {} for new

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/admin/articles/${form.id}`, form);
      else await api.post("/admin/articles", form);
      toast.success("Contenu enregistré.");
      setModal(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce contenu ?")) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Supprimé.");
    reload();
  };

  return (
    <div>
      <Title action={<button data-testid="new-article-btn" onClick={() => setModal({ ...emptyArticle })} className="inline-flex items-center gap-2 bg-brand-green text-white px-4 py-2.5 text-sm font-semibold hover:bg-brand-greenhover transition-colors"><FilePlus size={16} /> Nouveau contenu</button>}>Contenus</Title>
      {loading ? <Loader /> : (
        <div className="bg-white border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr><th className="px-4 py-3 font-semibold">Titre</th><th className="px-4 py-3 font-semibold">Catégorie</th><th className="px-4 py-3 font-semibold">Statut</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900 max-w-md truncate">{a.title}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{a.category}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 capitalize">{a.status}</span></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setModal(a)} className="text-slate-400 hover:text-brand-blue p-1.5"><Pencil size={16} /></button>
                    <button onClick={() => del(a.id)} data-testid={`del-article-${a.id}`} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">Aucun contenu.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {modal && <ArticleModal initial={modal} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

function ArticleModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Modal title={form.id ? "Modifier le contenu" : "Nouveau contenu"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-4">
        <Input label="Titre" value={form.title} onChange={set("title")} required testid="art-title" />
        <div className="grid grid-cols-2 gap-4">
          <Select label="Catégorie" value={form.category} onChange={set("category")} options={[["actualite", "Actualité"], ["communique", "Communiqué"], ["action", "Action terrain"]]} />
          <Select label="Statut" value={form.status} onChange={set("status")} options={[["draft", "Brouillon"], ["review", "Relecture"], ["published", "Publié"]]} />
        </div>
        <ImageField label="Image de couverture" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="art-image" />
        <Textarea label="Résumé" value={form.summary} onChange={set("summary")} rows={2} />
        <Textarea label="Contenu" value={form.content} onChange={set("content")} rows={6} />
        <button type="submit" data-testid="art-save" className="w-full bg-brand-green text-white py-3 font-semibold hover:bg-brand-greenhover transition-colors">Enregistrer</button>
      </form>
    </Modal>
  );
}

// ---------------- Events ----------------
const emptyEvent = { title: "", description: "", location: "", image_url: "", start_date: "", status: "published" };
function Events() {
  const { data, loading, reload } = useData("/admin/events");
  const [modal, setModal] = useState(null);
  const save = async (form) => {
    try {
      await api.post("/admin/events", form);
      toast.success("Événement créé.");
      setModal(null); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/events/${id}`); reload(); };
  return (
    <div>
      <Title action={<button data-testid="new-event-btn" onClick={() => setModal({ ...emptyEvent })} className="inline-flex items-center gap-2 bg-brand-green text-white px-4 py-2.5 text-sm font-semibold hover:bg-brand-greenhover transition-colors"><Plus size={16} /> Nouvel événement</button>}>Événements</Title>
      {loading ? <Loader /> : (
        <div className="space-y-3">
          {data.map((e) => (
            <div key={e.id} className="bg-white border border-border rounded-sm p-4 flex items-center justify-between">
              <div><p className="font-semibold text-slate-900">{e.title}</p><p className="text-sm text-slate-500">{new Date(e.start_date).toLocaleString("fr-FR")} · {e.location}</p></div>
              <button onClick={() => del(e.id)} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={16} /></button>
            </div>
          ))}
          {data.length === 0 && <p className="text-center text-slate-400 py-10">Aucun événement.</p>}
        </div>
      )}
      {modal && (
        <Modal title="Nouvel événement" onClose={() => setModal(null)}>
          <form onSubmit={(e) => { e.preventDefault(); save(modal); }} className="space-y-4">
            <Input label="Titre" value={modal.title} onChange={(e) => setModal({ ...modal, title: e.target.value })} required testid="ev-title" />
            <Textarea label="Description" value={modal.description} onChange={(e) => setModal({ ...modal, description: e.target.value })} rows={3} />
            <div className="grid grid-cols-2 gap-4">
              <Input label="Lieu" value={modal.location} onChange={(e) => setModal({ ...modal, location: e.target.value })} testid="ev-location" />
              <Input label="Date & heure" type="datetime-local" value={modal.start_date} onChange={(e) => setModal({ ...modal, start_date: e.target.value })} required testid="ev-date" />
            </div>
            <ImageField label="Image" value={modal.image_url} onChange={(v) => setModal({ ...modal, image_url: v })} testid="ev-image" />
            <button type="submit" data-testid="ev-save" className="w-full bg-brand-green text-white py-3 font-semibold hover:bg-brand-greenhover transition-colors">Enregistrer</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ---------------- Committees ----------------
const emptyComm = { name: "", region: "", city: "", lat: 14.69, lng: -17.44, responsable: "", contact: "", description: "" };
function Committees() {
  const { data, loading, reload } = useData("/admin/committees");
  const [modal, setModal] = useState(null);
  const save = async (form) => {
    try {
      const payload = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng) };
      if (form.id) await api.put(`/admin/committees/${form.id}`, payload);
      else await api.post("/admin/committees", payload);
      toast.success("Comité enregistré."); setModal(null); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/committees/${id}`); reload(); };
  return (
    <div>
      <Title action={<button data-testid="new-committee-btn" onClick={() => setModal({ ...emptyComm })} className="inline-flex items-center gap-2 bg-brand-green text-white px-4 py-2.5 text-sm font-semibold hover:bg-brand-greenhover transition-colors"><Plus size={16} /> Nouveau comité</button>}>Comités</Title>
      {loading ? <Loader /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.map((c) => (
            <div key={c.id} className="bg-white border border-border rounded-sm p-4">
              <div className="flex items-start justify-between">
                <div><p className="font-semibold text-slate-900">{c.name}</p><p className="text-sm text-slate-500">{c.city}, {c.region}</p></div>
                <div className="whitespace-nowrap">
                  <button onClick={() => setModal(c)} className="text-slate-400 hover:text-brand-blue p-1.5"><Pencil size={15} /></button>
                  <button onClick={() => del(c.id)} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-500">{c.responsable} · {c.contact}</p>
            </div>
          ))}
          {data.length === 0 && <p className="col-span-2 text-center text-slate-400 py-10">Aucun comité.</p>}
        </div>
      )}
      {modal && (
        <Modal title={modal.id ? "Modifier le comité" : "Nouveau comité"} onClose={() => setModal(null)}>
          <form onSubmit={(e) => { e.preventDefault(); save(modal); }} className="space-y-4">
            <Input label="Nom" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} required testid="com-name" />
            <div className="grid grid-cols-2 gap-4">
              <Select label="Région" value={modal.region} onChange={(e) => setModal({ ...modal, region: e.target.value })} options={[["", "Choisir"], ...REGIONS.map((r) => [r, r])]} />
              <Input label="Ville" value={modal.city} onChange={(e) => setModal({ ...modal, city: e.target.value })} testid="com-city" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Latitude" type="number" step="any" value={modal.lat} onChange={(e) => setModal({ ...modal, lat: e.target.value })} required testid="com-lat" />
              <Input label="Longitude" type="number" step="any" value={modal.lng} onChange={(e) => setModal({ ...modal, lng: e.target.value })} required testid="com-lng" />
            </div>
            <Input label="Responsable" value={modal.responsable} onChange={(e) => setModal({ ...modal, responsable: e.target.value })} testid="com-resp" />
            <Input label="Contact" value={modal.contact} onChange={(e) => setModal({ ...modal, contact: e.target.value })} testid="com-contact" />
            <Textarea label="Description" value={modal.description} onChange={(e) => setModal({ ...modal, description: e.target.value })} rows={2} />
            <button type="submit" data-testid="com-save" className="w-full bg-brand-green text-white py-3 font-semibold hover:bg-brand-greenhover transition-colors">Enregistrer</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ---------------- Bureau (COPIL) ----------------
const GROUP_OPTIONS = [
  ["presidence", "Présidence"],
  ["executif", "Bureau exécutif"],
  ["conseillers", "Conseillers stratégiques"],
  ["commissions", "Responsables de commissions"],
  ["sages", "Comité des Sages"],
];
const GROUP_LABEL = Object.fromEntries(GROUP_OPTIONS);
const emptyBureau = { name: "", role: "", group: "commissions", photo_url: "", bio: "", order: 100 };

function BureauTab() {
  const { data, loading, reload } = useData("/admin/bureau");
  const [modal, setModal] = useState(null);
  const save = async (form) => {
    try {
      const payload = { ...form, order: parseInt(form.order) || 100 };
      if (form.id) await api.put(`/admin/bureau/${form.id}`, payload);
      else await api.post("/admin/bureau", payload);
      toast.success("Membre enregistré."); setModal(null); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce membre ?")) return; await api.delete(`/admin/bureau/${id}`); toast.success("Supprimé."); reload(); };

  return (
    <div>
      <Title action={<button data-testid="new-bureau-btn" onClick={() => setModal({ ...emptyBureau })} className="inline-flex items-center gap-2 bg-brand-green text-white px-4 py-2.5 text-sm font-semibold hover:bg-brand-greenhover transition-colors"><Plus size={16} /> Nouveau membre</button>}>Le bureau (COPIL)</Title>
      {loading ? <Loader /> : data.length === 0 ? <Empty label="Aucun membre du bureau." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((m) => (
            <div key={m.id} data-testid={`bureau-row-${m.id}`} className="bg-white border border-border rounded-sm p-4 flex items-start gap-3">
              {m.photo_url
                ? <img src={m.photo_url} alt={m.name} className="h-14 w-14 rounded-full object-cover border border-border shrink-0" />
                : <div className="h-14 w-14 rounded-full bg-brand-blue/10 text-brand-blue font-bold flex items-center justify-center shrink-0">{m.name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()}</div>}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900 leading-tight">{m.name}</p>
                <p className="text-sm text-brand-green">{m.role}</p>
                <p className="text-xs text-slate-400 mt-1">{GROUP_LABEL[m.group] || m.group} · ordre {m.order}</p>
              </div>
              <div className="whitespace-nowrap">
                <button onClick={() => setModal(m)} className="text-slate-400 hover:text-brand-blue p-1.5"><Pencil size={15} /></button>
                <button onClick={() => del(m.id)} data-testid={`del-bureau-${m.id}`} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <Modal title={modal.id ? "Modifier le membre" : "Nouveau membre"} onClose={() => setModal(null)}>
          <form onSubmit={(e) => { e.preventDefault(); save(modal); }} className="space-y-4">
            <Input label="Nom complet" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} required testid="bur-name" />
            <Textarea label="Rôle / Fonction (une par ligne)" value={modal.role} onChange={(e) => setModal({ ...modal, role: e.target.value })} rows={2} />
            <div className="grid grid-cols-2 gap-4">
              <Select label="Groupe" value={modal.group} onChange={(e) => setModal({ ...modal, group: e.target.value })} options={GROUP_OPTIONS} />
              <Input label="Ordre d'affichage" type="number" value={modal.order} onChange={(e) => setModal({ ...modal, order: e.target.value })} testid="bur-order" />
            </div>
            <ImageField label="Photo" value={modal.photo_url} onChange={(v) => setModal({ ...modal, photo_url: v })} testid="bur-photo" />
            <Textarea label="Biographie" value={modal.bio} onChange={(e) => setModal({ ...modal, bio: e.target.value })} rows={4} />
            <button type="submit" data-testid="bur-save" className="w-full bg-brand-green text-white py-3 font-semibold hover:bg-brand-greenhover transition-colors">Enregistrer</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ---------------- Memberships ----------------
function Memberships() {
  const { data, loading, reload } = useData("/admin/memberships");
  const [cardModal, setCardModal] = useState(null);
  const [sending, setSending] = useState(false);
  const setStatus = async (id, status) => {
    await api.put(`/admin/memberships/${id}/status`, null, { params: { status } });
    toast.success("Statut mis à jour."); reload();
  };
  const sendCard = async (dataUrl) => {
    setSending(true);
    try {
      const { data: res } = await api.post(`/admin/memberships/${cardModal.id}/send-card`, { image_base64: dataUrl || "" });
      toast.success(`Carte envoyée à ${res.email}.`);
      setCardModal(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };
  return (
    <div>
      <Title action={<button onClick={() => exportCSV(data, "adhesions.csv")} data-testid="export-memberships" className="inline-flex items-center gap-2 border border-border bg-white px-4 py-2.5 text-sm font-semibold hover:bg-slate-50 transition-colors"><Download size={16} /> Exporter CSV</button>}>Adhésions</Title>
      {loading ? <Loader /> : (
        <div className="bg-white border border-border rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr><th className="px-4 py-3 font-semibold">N° adhérent</th><th className="px-4 py-3 font-semibold">Nom</th><th className="px-4 py-3 font-semibold">Type</th><th className="px-4 py-3 font-semibold">Région</th><th className="px-4 py-3 font-semibold">Statut</th><th className="px-4 py-3 font-semibold">Carte</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((m) => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{m.member_number}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{m.full_name}<br /><span className="text-xs text-slate-400">{m.email}</span></td>
                  <td className="px-4 py-3 capitalize text-slate-600">{m.membership_type}</td>
                  <td className="px-4 py-3 text-slate-600">{m.region || "—"}</td>
                  <td className="px-4 py-3">
                    <select value={m.status} onChange={(e) => setStatus(m.id, e.target.value)} className="text-xs border border-border px-2 py-1 rounded-sm">
                      <option value="en_attente">En attente</option>
                      <option value="valide">Validé</option>
                      <option value="rejete">Rejeté</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setCardModal(m)} data-testid={`card-btn-${m.id}`} className="inline-flex items-center gap-1.5 text-brand-blue hover:text-brand-green font-semibold text-xs">
                      <IdCard size={15} /> Carte
                    </button>
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Aucune adhésion.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {cardModal && (
        <Modal title={`Carte de ${cardModal.full_name}`} onClose={() => setCardModal(null)}>
          <div className="max-w-sm mx-auto">
            <MemberCard membership={cardModal} name={cardModal.full_name} onSendCard={sendCard} sending={sending} />
            <p className="mt-4 text-xs text-slate-400 text-center">L'envoi nécessite un SMTP configuré (Paramètres). L'email contient le n°, le lien de vérification et la carte en pièce jointe.</p>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------- Contacts ----------------
function Contacts() {
  const { data, loading } = useData("/admin/contacts");
  return (
    <div>
      <Title action={<button onClick={() => exportCSV(data, "messages.csv")} data-testid="export-contacts" className="inline-flex items-center gap-2 border border-border bg-white px-4 py-2.5 text-sm font-semibold hover:bg-slate-50 transition-colors"><Download size={16} /> Exporter CSV</button>}>Messages reçus</Title>
      {loading ? <Loader /> : data.length === 0 ? <Empty label="Aucun message." /> : (
        <div className="space-y-3">
          {data.map((c) => (
            <div key={c.id} className="bg-white border border-border rounded-sm p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-slate-900">{c.full_name} {c.is_press && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 ml-1">Presse</span>}</p>
                  <p className="text-sm text-slate-500">{c.email} {c.phone && `· ${c.phone}`}</p>
                </div>
                <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
              </div>
              {c.subject && <p className="mt-2 text-sm font-medium text-slate-700">{c.subject}</p>}
              <p className="mt-1 text-sm text-slate-600">{c.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Newsletter ----------------
function Newsletter() {
  const { data, loading } = useData("/admin/newsletter");
  const [form, setForm] = useState({ subject: "", message: "", region: "", test_email: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const targetCount = form.region ? data.filter((n) => n.region === form.region).length : data.length;

  const send = async (test) => {
    if (!form.subject.trim() || !form.message.trim()) return toast.error("Renseignez l'objet et le message.");
    if (test && !form.test_email.trim()) return toast.error("Indiquez une adresse de test.");
    if (!test && !window.confirm(`Envoyer cette campagne à ${targetCount} abonné(s) ?`)) return;
    setSending(true);
    try {
      const payload = { subject: form.subject, message: form.message, region: form.region };
      if (test) payload.test_email = form.test_email;
      const { data: res } = await api.post("/admin/newsletter/send", payload);
      if (res.test) toast.success("Email de test envoyé !");
      else toast.success(`Campagne envoyée : ${res.sent}/${res.total} (${res.failed} échec(s)).`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  return (
    <div>
      <Title action={<button onClick={() => exportCSV(data, "newsletter.csv")} data-testid="export-newsletter" className="inline-flex items-center gap-2 border border-border bg-white px-4 py-2.5 text-sm font-semibold hover:bg-slate-50 transition-colors"><Download size={16} /> Exporter CSV</button>}>Newsletter</Title>

      <div className="bg-white border border-border rounded-sm p-6 mb-8">
        <h2 className="font-bold text-slate-900 flex items-center gap-2"><Send size={18} /> Composer une campagne</h2>
        <p className="text-sm text-slate-500 mt-1">Envoyez un email à vos abonnés via le SMTP configuré (onglet Paramètres).</p>
        <div className="mt-5 space-y-4">
          <Input label="Objet" value={form.subject} onChange={set("subject")} testid="nl-subject" />
          <Textarea label="Message" value={form.message} onChange={set("message")} rows={7} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select label="Cibler une région (optionnel)" value={form.region} onChange={set("region")} options={[["", `Tous les abonnés (${data.length})`], ...REGIONS.map((r) => [r, r])]} />
            <Input label="Email de test" value={form.test_email} onChange={set("test_email")} testid="nl-test-email" />
          </div>
          <p className="text-xs text-slate-500">Destinataires de la campagne : <span className="font-semibold text-slate-700">{targetCount}</span></p>
          <div className="flex flex-wrap gap-3 pt-1">
            <button onClick={() => send(true)} disabled={sending} data-testid="nl-send-test" className="inline-flex items-center gap-2 border border-border px-5 py-2.5 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60">
              {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Envoyer un test
            </button>
            <button onClick={() => send(false)} disabled={sending} data-testid="nl-send-all" className="inline-flex items-center gap-2 bg-brand-green text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-greenhover transition-colors disabled:opacity-60">
              {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Envoyer à {targetCount} abonné(s)
            </button>
          </div>
        </div>
      </div>

      <h2 className="font-bold text-slate-900 mb-3">Abonnés ({data.length})</h2>
      {loading ? <Loader /> : data.length === 0 ? <Empty label="Aucun abonné." /> : (
        <div className="bg-white border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500"><tr><th className="px-4 py-3 font-semibold">Email</th><th className="px-4 py-3 font-semibold">Région</th><th className="px-4 py-3 font-semibold">Inscrit le</th></tr></thead>
            <tbody className="divide-y divide-border">
              {data.map((n) => (
                <tr key={n.id} className="hover:bg-slate-50"><td className="px-4 py-3 text-slate-900">{n.email}</td><td className="px-4 py-3 text-slate-600">{n.region || "—"}</td><td className="px-4 py-3 text-slate-500">{new Date(n.created_at).toLocaleDateString("fr-FR")}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------- Requests ----------------
function Requests() {
  const { data, loading } = useData("/admin/committee-requests");
  return (
    <div>
      <Title>Demandes de création de comités</Title>
      {loading ? <Loader /> : data.length === 0 ? <Empty label="Aucune demande." /> : (
        <div className="space-y-3">
          {data.map((r) => (
            <div key={r.id} className="bg-white border border-border rounded-sm p-5">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-900">{r.name} — {r.region}{r.city && `, ${r.city}`}</p>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 capitalize">{r.status}</span>
              </div>
              <p className="mt-2 text-sm text-slate-500">Demandeur : {r.full_name} · {r.email} {r.phone && `· ${r.phone}`}</p>
              {r.motivation && <p className="mt-2 text-sm text-slate-600">{r.motivation}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Media library ----------------
function Media() {
  const { data, loading, reload } = useData("/admin/media");
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fichier téléversé.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce média ?")) return;
    await api.delete(`/admin/media/${id}`);
    reload();
  };

  const copy = (url, id) => {
    navigator.clipboard.writeText(mediaUrl(url));
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
    toast.success("URL copiée.");
  };

  return (
    <div>
      <Title action={
        <label data-testid="media-upload-btn" className="inline-flex items-center gap-2 bg-brand-green text-white px-4 py-2.5 text-sm font-semibold hover:bg-brand-greenhover transition-colors cursor-pointer">
          {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} Téléverser
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onUpload} disabled={uploading} />
        </label>
      }>Bibliothèque média</Title>
      {loading ? <Loader /> : data.length === 0 ? <Empty label="Aucun média. Téléversez images et PDF." /> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {data.map((m) => {
            const isImg = (m.content_type || "").startsWith("image/");
            return (
              <div key={m.id} data-testid={`media-${m.id}`} className="bg-white border border-border rounded-sm overflow-hidden">
                <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
                  {isImg ? <img src={mediaUrl(m.url)} alt={m.original_filename} className="w-full h-full object-cover" />
                         : <FileText size={40} className="text-slate-400" />}
                </div>
                <div className="p-3">
                  <p className="text-xs text-slate-600 truncate" title={m.original_filename}>{m.original_filename}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <button onClick={() => copy(m.url, m.id)} className="flex-1 inline-flex items-center justify-center gap-1 text-xs border border-border py-1.5 hover:bg-slate-50">
                      {copied === m.id ? <Check size={13} className="text-brand-green" /> : <Copy size={13} />} URL
                    </button>
                    <button onClick={() => del(m.id)} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------- Social publisher ----------------
const PLATFORM_META = {
  facebook: { label: "Facebook", Icon: Facebook },
  instagram: { label: "Instagram", Icon: Instagram },
  x: { label: "X (Twitter)", Icon: Twitter },
  tiktok: { label: "TikTok", Icon: Music2 },
  youtube: { label: "YouTube", Icon: Youtube },
};

function SocialPublisher() {
  const [social, setSocial] = useState(null);
  const [posts, setPosts] = useState([]);
  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [selected, setSelected] = useState([]);
  const [publishing, setPublishing] = useState(false);

  const loadPosts = () => api.get("/admin/social/posts").then(({ data }) => setPosts(data));

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setSocial(data.social));
    loadPosts();
  }, []);

  if (!social) return <Loader />;

  const toggle = (p) => setSelected((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));

  const publish = async () => {
    if (!message.trim()) return toast.error("Écrivez un message.");
    if (selected.length === 0) return toast.error("Sélectionnez au moins un réseau.");
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/social/publish", { message, image_url: imageUrl, platforms: selected });
      const okCount = Object.values(data.results).filter((r) => r.ok).length;
      if (okCount > 0) toast.success(`Publié sur ${okCount} réseau(x).`);
      const failed = Object.entries(data.results).filter(([, r]) => !r.ok);
      if (failed.length) toast.error(`Échecs : ${failed.map(([p]) => PLATFORM_META[p]?.label).join(", ")}`);
      setMessage(""); setImageUrl(""); setSelected([]);
      loadPosts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setPublishing(false); }
  };

  return (
    <div>
      <Title>Publications multi-réseaux</Title>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <div className="bg-white border border-border rounded-sm p-6">
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Message</label>
            <textarea data-testid="social-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={5} placeholder="Rédigez votre publication…" className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
            <div className="mt-4">
              <ImageField label="Image (optionnelle)" value={imageUrl} onChange={setImageUrl} testid="social-image" />
            </div>
            <p className="mt-5 text-sm font-semibold text-slate-700 mb-2">Diffuser sur :</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PLATFORM_META).map(([key, { label, Icon }]) => {
                const enabled = social[key]?.enabled;
                const active = selected.includes(key);
                return (
                  <button
                    key={key}
                    data-testid={`social-target-${key}`}
                    disabled={!enabled}
                    onClick={() => toggle(key)}
                    title={enabled ? "" : "À configurer dans Paramètres"}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-2 transition-colors
                      ${active ? "border-brand-green bg-brand-green/10 text-brand-green" : "border-border text-slate-600"}
                      ${!enabled ? "opacity-40 cursor-not-allowed" : "hover:border-brand-green"}`}
                  >
                    <Icon size={16} /> {label}
                  </button>
                );
              })}
            </div>
            {!Object.values(social).some((s) => s.enabled) && (
              <p className="mt-3 text-xs text-amber-600">Aucun réseau connecté. Configurez-les dans l'onglet Paramètres → Réseaux sociaux.</p>
            )}
            <button onClick={publish} disabled={publishing} data-testid="social-publish-btn" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-green text-white py-3 font-semibold hover:bg-brand-greenhover transition-colors disabled:opacity-60">
              {publishing ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Publier maintenant
            </button>
          </div>
        </div>

        <div className="lg:col-span-5">
          <h3 className="font-bold text-slate-900 mb-3">Historique des publications</h3>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {posts.length === 0 && <Empty label="Aucune publication." />}
            {posts.map((p) => (
              <div key={p.id} className="bg-white border border-border rounded-sm p-4">
                <p className="text-sm text-slate-800 line-clamp-3">{p.message}</p>
                {p.image_url && <img src={p.image_url} alt="" className="mt-2 h-16 rounded-sm object-cover" />}
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(p.results || {}).map(([plat, r]) => {
                    const M = PLATFORM_META[plat];
                    return (
                      <span key={plat} className={`inline-flex items-center gap-1 text-xs px-2 py-1 ${r.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`} title={r.error || ""}>
                        {M && <M.Icon size={12} />} {r.ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                      </span>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-slate-400">{new Date(p.created_at).toLocaleString("fr-FR")}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Settings (SMTP + payments) ----------------
function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setSettings(data)); }, []);
  if (!settings) return <Loader />;

  const smtp = settings.smtp;
  const pay = settings.payments;
  const soc = settings.social || {};
  const setSmtp = (k, v) => setSettings({ ...settings, smtp: { ...smtp, [k]: v } });
  const setPay = (k, v) => setSettings({ ...settings, payments: { ...pay, [k]: v } });
  const setSoc = (platform, k, v) => setSettings({ ...settings, social: { ...soc, [platform]: { ...soc[platform], [k]: v } } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { smtp, payments: pay });
      await api.put("/admin/social/config", { social: soc });
      toast.success("Paramètres enregistrés.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/admin/settings/test-email", { to_email: testEmail });
      toast.success("Email de test envoyé !");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setTesting(false); }
  };

  return (
    <div className="max-w-2xl">
      <Title>Paramètres</Title>

      <section className="bg-white border border-border rounded-sm p-6 mb-6">
        <h2 className="font-bold text-slate-900 flex items-center gap-2"><Mail size={18} /> Configuration SMTP (emails)</h2>
        <p className="text-sm text-slate-500 mt-1">Utilisée pour les reçus de paiement et confirmations d'adhésion.</p>
        <div className="mt-5 grid grid-cols-2 gap-4">
          <Input label="Serveur SMTP" value={smtp.host} onChange={(e) => setSmtp("host", e.target.value)} testid="smtp-host" />
          <Input label="Port" type="number" value={smtp.port} onChange={(e) => setSmtp("port", parseInt(e.target.value) || 587)} testid="smtp-port" />
          <Input label="Identifiant" value={smtp.username} onChange={(e) => setSmtp("username", e.target.value)} testid="smtp-username" />
          <Input label="Mot de passe" type="password" value={smtp.password} onChange={(e) => setSmtp("password", e.target.value)} testid="smtp-password" />
          <Input label="Nom expéditeur" value={smtp.from_name} onChange={(e) => setSmtp("from_name", e.target.value)} testid="smtp-fromname" />
          <Input label="Email expéditeur" value={smtp.from_email} onChange={(e) => setSmtp("from_email", e.target.value)} testid="smtp-fromemail" />
        </div>
        <label className="flex items-center gap-2 mt-4 text-sm text-slate-700">
          <input type="checkbox" checked={smtp.use_tls} onChange={(e) => setSmtp("use_tls", e.target.checked)} className="h-4 w-4 accent-brand-green" /> Utiliser TLS (recommandé)
        </label>
        <div className="mt-5 flex items-end gap-3 border-t border-border pt-5">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Envoyer un email de test à</label>
            <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="test-email-input" placeholder="email@exemple.com" className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
          </div>
          <button onClick={sendTest} disabled={testing || !testEmail} data-testid="test-email-btn" className="inline-flex items-center gap-2 border border-border px-4 py-2.5 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60">
            {testing ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Tester
          </button>
        </div>
      </section>

      <section className="bg-white border border-border rounded-sm p-6 mb-6">
        <h2 className="font-bold text-slate-900 flex items-center gap-2"><CreditCard size={18} /> Moyens de paiement</h2>
        <p className="text-sm text-slate-500 mt-1">Activez les méthodes proposées aux membres.</p>
        <div className="mt-5 space-y-4">
          <Toggle label="Stripe (carte bancaire)" desc="Paiement par carte — actif." checked={pay.stripe} onChange={(v) => setPay("stripe", v)} testid="toggle-stripe" />
          <div>
            <Toggle label="Wave" desc="Mobile money. Saisissez votre clé API marchand." checked={pay.wave} onChange={(v) => setPay("wave", v)} testid="toggle-wave" />
            {pay.wave && <Input label="Clé API Wave" value={pay.wave_api_key} onChange={(e) => setPay("wave_api_key", e.target.value)} testid="wave-key" />}
          </div>
          <div>
            <Toggle label="Orange Money" desc="Mobile money. Saisissez votre clé API marchand." checked={pay.orange_money} onChange={(v) => setPay("orange_money", v)} testid="toggle-om" />
            {pay.orange_money && <Input label="Clé API Orange Money" value={pay.orange_money_api_key} onChange={(e) => setPay("orange_money_api_key", e.target.value)} testid="om-key" />}
          </div>
        </div>
        <p className="text-xs text-amber-600 mt-4">Note : l'encaissement Wave / Orange Money sera finalisé avec vos clés marchandes ; Stripe est pleinement fonctionnel.</p>
      </section>

      <section className="bg-white border border-border rounded-sm p-6 mb-6">
        <h2 className="font-bold text-slate-900 flex items-center gap-2"><Share2 size={18} /> Réseaux sociaux (publication)</h2>
        <p className="text-sm text-slate-500 mt-1">Connectez vos comptes pour publier depuis l'onglet « Publications ».</p>

        <div className="mt-5 space-y-5">
          <div className="border border-border rounded-sm p-4">
            <Toggle label="Facebook (Page)" desc="Pleinement fonctionnel." checked={!!soc.facebook?.enabled} onChange={(v) => setSoc("facebook", "enabled", v)} testid="soc-facebook-toggle" />
            {soc.facebook?.enabled && (
              <div className="mt-3 grid grid-cols-1 gap-3">
                <Input label="Page ID" value={soc.facebook?.page_id || ""} onChange={(e) => setSoc("facebook", "page_id", e.target.value)} testid="fb-page-id" />
                <Input label="Jeton d'accès Page (long-lived)" value={soc.facebook?.page_token || ""} onChange={(e) => setSoc("facebook", "page_token", e.target.value)} testid="fb-page-token" />
                <p className="text-xs text-slate-400">Obtenez-les via Meta for Developers → Graph API Explorer (permissions pages_manage_posts).</p>
              </div>
            )}
          </div>

          {[["instagram", "Instagram"], ["x", "X (Twitter)"], ["tiktok", "TikTok"], ["youtube", "YouTube"]].map(([key, label]) => (
            <div key={key} className="border border-border rounded-sm p-4">
              <Toggle label={label} desc="Connexion à finaliser avec vos identifiants développeur." checked={!!soc[key]?.enabled} onChange={(v) => setSoc(key, "enabled", v)} testid={`soc-${key}-toggle`} />
              {soc[key]?.enabled && (
                <div className="mt-3">
                  <Input label="Jeton d'accès / clé API" value={soc[key]?.access_token || ""} onChange={(e) => setSoc(key, "access_token", e.target.value)} testid={`${key}-token`} />
                  <p className="text-xs text-amber-600 mt-2">La publication automatique sur {label} sera activée une fois l'API développeur configurée.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <button onClick={save} disabled={saving} data-testid="settings-save" className="bg-brand-green text-white px-6 py-3 font-semibold hover:bg-brand-greenhover transition-colors disabled:opacity-60">
        {saving ? "Enregistrement..." : "Enregistrer les paramètres"}
      </button>
    </div>
  );
}

const Toggle = ({ label, desc, checked, onChange, testid }) => (
  <div className="flex items-center justify-between gap-4">
    <div>
      <p className="font-medium text-slate-900">{label}</p>
      <p className="text-xs text-slate-500">{desc}</p>
    </div>
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${checked ? "bg-brand-green" : "bg-slate-300"}`}>
      <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  </div>
);

// ---------------- Shared UI ----------------
const Loader = () => <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand-blue" size={28} /></div>;
const Empty = ({ label }) => <div className="bg-white border border-border rounded-sm py-16 text-center text-slate-400"><Inbox size={32} className="mx-auto mb-3" />{label}</div>;

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-sm max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose}><X size={20} className="text-slate-400" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const Input = ({ label, value, onChange, type = "text", required, testid, step }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}{required && " *"}</label>
    <input type={type} step={step} required={required} value={value} onChange={onChange} data-testid={testid} className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
  </div>
);
const Textarea = ({ label, value, onChange, rows }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
    <textarea value={value} onChange={onChange} rows={rows} className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
  </div>
);
const Select = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
    <select value={value} onChange={onChange} className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

const ImageField = ({ label = "Image", value, onChange, testid }) => {
  const [up, setUp] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUp(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(mediaUrl(data.url));
      toast.success("Image téléversée.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUp(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
      <div className="flex gap-2">
        <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder="URL ou téléverser une image" className="flex-1 border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
        <label className="inline-flex items-center gap-1 border border-border px-3 text-sm cursor-pointer hover:bg-slate-50">
          {up ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />}
          <input type="file" accept="image/*" className="hidden" onChange={handle} disabled={up} />
        </label>
      </div>
      {value ? <img src={value} alt="" className="mt-2 h-20 rounded-sm object-cover border border-border" /> : null}
    </div>
  );
};
